// Designer (DQ-STU, Kap. 15.3): freie Folien (specVersion 2) – Bausteine, Vorlagen, Verschieben/Grösse ändern mit
// Einrasten und Hilfslinien, Pfeiltasten, Entf, Ebenen, Duplizieren. Darstellung mit @disploraq/layouts.
import {
  CLOCK_FACES, CLOCK_TIME_ZONES, createElement, faceColors, isAnalogFace, TEMPLATES, WIDGET_TYPES, type ElementType, type LayoutElement, type LayoutV2,
} from '@disploraq/layouts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { can, qk, useLayout, useLayouts, useMe } from '../api/queries';
import type { LayoutRow, Media } from '../api/types';
import { usePendingFor } from '../api/approvals';
import { useDataSourceData, useDataSources } from '../api/datasources';
import { PendingPill, SubmitDialog, useApprovalRequired } from '../components/Approval';
import { Icon, WIDGET_ICONS } from '../components/Icon';
import { LayoutPreview, mediaUrl, useMediaIndex, type MediaIndex } from '../components/Preview';
import { PlaceSearch } from '../components/PlaceSearch';
import { useToast } from '../components/toast';
import { Spinner } from '../components/ui';
import { openPreview } from '../preview/open';
import { PrayerFields } from './prayer/PrayerFields';
import { EditCopyButton, EmbedProps, ReadonlyPanel, SplitButtons, splitElements, type SplitKind } from './template-edit/TemplateEdit';
import { disabledWidgets, widgetAllowed } from '../api/features';
import { AssetSelect, UploadArea, useStudioUploads, type StudioUploads } from './studio/StudioUpload';

type El = LayoutElement & { id: string };
type Def = Omit<LayoutV2, 'elements'> & { elements: El[] };
type Handle = 'nw' | 'ne' | 'sw' | 'se' | null;
type Guides = { x: number | null; y: number | null };

const uid = () => Math.random().toString(36).slice(2, 10);
const round = (v: number) => Math.round(v * 10) / 10;

/** Bausteine mit Mediathek: Medien werden per sha256 referenziert (asset/assets), nicht per Adresse. */
export function newElement(type: ElementType, media: Media[], base: Partial<LayoutElement> = {}): El {
  const el = createElement(type, 30, 30, 40, 40, base as never) as El;
  el.id = uid();
  const imgs = media.filter((m) => m.kind === 'image' && m.current);
  if (type === 'image' && 'src' in el) delete el.src;
  if (type === 'video' && 'src' in el) delete el.src;
  if (type === 'slideshow' && 'srcs' in el) delete el.srcs;
  if (type === 'ticker' || type === 'feed') Object.assign(el, { x: 0, y: 88, w: 100, h: 12 }, base);
  if (el.type === 'image' && !el.asset && imgs[0]) el.asset = imgs[0].current!.sha256;
  if (el.type === 'video' && !el.asset) { const v = media.find((m) => m.kind === 'video' && m.current); if (v) el.asset = v.current!.sha256; }
  if (el.type === 'slideshow' && !el.assets?.length) el.assets = imgs.slice(0, 8).map((m) => m.current!.sha256);
  return el;
}

const withIds = (d: unknown): Def => {
  const l = (d ?? {}) as LayoutV2;
  return { ...l, specVersion: 2, elements: (l.elements ?? []).map((e) => ({ ...e, id: e.id ?? uid() })) as El[] };
};

/** Einrasten an Rändern, Mitte und Kanten anderer Elemente (wie im Prototyp, Toleranz 1,2 %). */
export function snap(v: number, list: number[]): [number, number | null] {
  for (const s of list) if (Math.abs(v - s) < 1.2) return [s, s];
  return [Math.round(v * 2) / 2, null];
}

export function StudioPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const back = params.get('back') ?? '/templates';
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: layout, isLoading } = useLayout(id);
  // Baustein „Vorlage": eingebettete Vorlagen darstellen und auswählen
  const { data: allLayouts } = useLayouts();
  const media = useMediaIndex();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [def, setDef] = useState<Def | null>(null);
  const [name, setName] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<El | null>(null);
  const [guides, setGuides] = useState<Guides>({ x: null, y: null });
  // Freigabe-Workflow: nach dem Speichern einreichen statt veröffentlichen
  const approval = useApprovalRequired();
  const pending = usePendingFor('layout', id);
  const [submit, setSubmit] = useState(false);
  const version = useRef(0);
  const frame = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (layout && def === null) {
      setDef(withIds(layout.definition));
      setName(layout.name);
      version.current = layout.rowVersion;
    }
  }, [layout, def]);

  const change = useCallback((fn: (d: Def) => Def) => {
    setDef((d) => (d ? fn(d) : d));
    setDirty(true);
  }, []);
  const patchEl = useCallback((elId: string, p: Partial<LayoutElement>) => change((d) => ({ ...d, elements: d.elements.map((e) => (e.id === elId ? { ...e, ...p } as El : e)) })), [change]);
  // Hochladen im Baustein: nach der Prüfung automatisch wählen (Slideshow: anhängen)
  const uploads = useStudioUploads(media.list, (u, sha) => change((d) => ({
    ...d,
    elements: d.elements.map((e) => {
      if (e.id !== u.elId) return e;
      if (u.field === 'assets' && e.type === 'slideshow') return { ...e, assets: [...(e.assets ?? []).filter((x) => x !== sha), sha] };
      return { ...e, asset: sha } as El;
    }),
  })));
  const remove = useCallback(() => {
    if (!sel) return;
    change((d) => ({ ...d, elements: d.elements.filter((e) => e.id !== sel) }));
    setSel(null);
  }, [sel, change]);

  // Tastatur: Pfeile verschieben (Umschalt = 5 %), Entf löscht
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? '').toUpperCase();
      if (!sel || !def || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      const el = def.elements.find((x) => x.id === sel);
      if (!el) return;
      const step = e.shiftKey ? 5 : 0.5;
      const k = ({ ArrowLeft: ['x', -step], ArrowRight: ['x', step], ArrowUp: ['y', -step], ArrowDown: ['y', step] } as Record<string, ['x' | 'y', number]>)[e.key];
      if (k) {
        e.preventDefault();
        const max = 100 - (k[0] === 'x' ? el.w : el.h);
        patchEl(el.id, { [k[0]]: Math.max(0, Math.min(max, el[k[0]] + k[1])) });
      }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); remove(); }
      if (e.key === 'Escape') setSel(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sel, def, patchEl, remove]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (isLoading || (layout && !def)) return <div className="content"><Spinner /></div>;
  if (!layout || !def) return <div className="content card muted">{t('common.notFound')}</div>;
  const editable = layout.scope === 'tenant' && can(me, 'layouts.manage') && (layout.definition as { specVersion?: number }).specVersion === 2;

  const startDrag = (e: RPointerEvent<HTMLDivElement>, el: El) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    setSel(el.id);
    const handle = ((e.target as HTMLElement).dataset.h ?? null) as Handle;
    const rect = frame.current!.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, el: { ...el } };
    const others = def.elements.filter((o) => o.id !== el.id);
    const sx = [0, 50, 100, ...others.flatMap((o) => [o.x, o.x + o.w])];
    const sy = [0, 50, 100, ...others.flatMap((o) => [o.y, o.y + o.h])];
    const canvasBox = frame.current!.querySelector<HTMLElement>(`.wl-canvas [data-id="${CSS.escape(el.id)}"]`);
    let cur = { ...el };
    let moved = false;
    const move = (ev: PointerEvent) => {
      moved = true;
      const dx = ((ev.clientX - start.x) / rect.width) * 100;
      const dy = ((ev.clientY - start.y) / rect.height) * 100;
      const s = start.el;
      let gx: number | null = null;
      let gy: number | null = null;
      const n = { ...cur };
      if (!handle) {
        const [x1, g1] = snap(s.x + dx, sx);
        const [y1, g2] = snap(s.y + dy, sy);
        let x = x1;
        let y = y1;
        const [x2, g3] = snap(s.x + dx + s.w, sx);
        const [y2, g4] = snap(s.y + dy + s.h, sy);
        if (g1 === null && g3 !== null) { x = x2 - s.w; gx = g3; } else gx = g1;
        if (g2 === null && g4 !== null) { y = y2 - s.h; gy = g4; } else gy = g2;
        n.x = Math.min(100 - s.w, Math.max(0, x));
        n.y = Math.min(100 - s.h, Math.max(0, y));
      } else {
        if (handle.includes('e')) { const [r, g] = snap(s.x + s.w + dx, sx); n.w = Math.max(3, Math.min(100 - s.x, r - s.x)); gx = g; }
        if (handle.includes('s')) { const [b, g] = snap(s.y + s.h + dy, sy); n.h = Math.max(3, Math.min(100 - s.y, b - s.y)); gy = g; }
        if (handle.includes('w')) { const [l, g] = snap(s.x + dx, sx); const nx = Math.max(0, Math.min(s.x + s.w - 3, l)); n.w = s.w + s.x - nx; n.x = nx; gx = g; }
        if (handle.includes('n')) { const [tp, g] = snap(s.y + dy, sy); const ny = Math.max(0, Math.min(s.y + s.h - 3, tp)); n.h = s.h + s.y - ny; n.y = ny; gy = g; }
      }
      cur = n;
      if (canvasBox) Object.assign(canvasBox.style, { left: `${n.x}%`, top: `${n.y}%`, width: `${n.w}%`, height: `${n.h}%` });
      setLive(n);
      setGuides({ x: gx, y: gy });
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setGuides({ x: null, y: null });
      setLive(null);
      if (moved) patchEl(el.id, { x: cur.x, y: cur.y, w: cur.w, h: cur.h });
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const add = (type: ElementType) => {
    const el = newElement(type, media.list);
    if (type !== 'ticker') { const k = def.elements.length % 6; el.x += k * 4 - 10; el.y += k * 4 - 10; }
    change((d) => ({ ...d, elements: [...d.elements, el] }));
    setSel(el.id);
  };
  const applyTemplate = (tplName: string) => {
    const tpl = TEMPLATES[tplName];
    if (!tpl) return;
    if (def.elements.length && !window.confirm(t('studio.replaceConfirm'))) return;
    const imgs = media.list.filter((m) => m.kind === 'image' && m.current);
    let n = 0;
    change((d) => ({
      ...d,
      elements: tpl().map((x) => {
        const el = newElement(x.type, media.list, x);
        if (el.type === 'image') { delete el.src; const m = imgs[n++ % Math.max(1, imgs.length)]; if (m) el.asset = m.current!.sha256; }
        if (el.type === 'slideshow') { delete el.srcs; el.assets = imgs.slice(0, 8).map((m) => m.current!.sha256); }
        return el;
      }),
    }));
    setSel(null);
  };
  const layer = (dir: number) => change((d) => {
    const a = [...d.elements];
    const i = a.findIndex((e) => e.id === sel);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= a.length) return d;
    [a[i], a[j]] = [a[j]!, a[i]!];
    return { ...d, elements: a };
  });
  const duplicate = () => {
    const src = def.elements.find((e) => e.id === sel);
    if (!src) return;
    const el = { ...structuredClone(src), id: uid() };
    el.x = Math.min(100 - el.w, el.x + 3);
    el.y = Math.min(100 - el.h, el.y + 3);
    change((d) => ({ ...d, elements: [...d.elements, el] }));
    setSel(el.id);
  };

  const save = async (publish: boolean): Promise<boolean> => {
    setBusy(true);
    try {
      const definition: LayoutV2 = { ...def, elements: def.elements };
      const res = await api.put<LayoutRow>(`/layouts/${id}`, { name: name.trim() || layout.name, definition, orientation: layout.orientation }, { ifMatch: version.current });
      version.current = res.rowVersion;
      if (publish && approval) setSubmit(true);
      else if (publish) await api.post(`/layouts/${id}/publish`);
      setDirty(false);
      await qc.invalidateQueries({ queryKey: qk.layouts });
      if (!(publish && approval)) toast.show(publish ? t('studio.published') : t('common.saved'));
      if (publish && !approval && params.get('back')) navigate(back);
      return true;
    } catch (e) {
      toast.error(e);
      return false;
    } finally {
      setBusy(false);
    }
  };
  // M20: Vorschau zeigt den gespeicherten Entwurf – ungespeicherte Änderungen vorher speichern
  const preview = () => openPreview('layout', id, dirty && editable ? async () => {
    if (!(await save(false))) throw new Error('save failed');
  } : undefined).catch(() => {});
  // Aufteilen: Vorhandenes in den Hauptbereich, daneben/darunter ein neuer Bereich
  const split = (k: SplitKind) => {
    const r = splitElements(def.elements, k, t('tedit.area'));
    change((d) => ({ ...d, elements: r.elements }));
    setSel(r.added);
  };

  const selected = def.elements.find((e) => e.id === sel);
  const shown = (e: El) => (live && live.id === e.id ? live : e);
  const images = media.list.filter((m) => m.kind === 'image' && m.current?.status === 'ready');

  return (
    <div className="designer" data-testid="studio">
      <div className="d-top">
        <Link className="btn ghost icon" to={back} aria-label={t('common.back')} onClick={(e) => { if (dirty && !window.confirm(t('design.discard'))) e.preventDefault(); }}><Icon name="left" /></Link>
        <input aria-label={t('common.name')} value={name} disabled={!editable} onChange={(e) => { setName(e.target.value); setDirty(true); }}
          placeholder={t('studio.namePh')} style={{ fontWeight: 600, minWidth: 160 }} />
        {editable && (
          <select aria-label={t('studio.applyTemplate')} value="" onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">{t('studio.applyTemplate')}</option>
            {Object.keys(TEMPLATES).filter((k) => !disabledWidgets(me, TEMPLATES[k]).length).map((k) => <option key={k} value={k}>{t(`studio.tpl.${k}`, { defaultValue: k })}</option>)}
          </select>
        )}
        {editable && (
          <label className="row muted small" style={{ gap: 6 }}>{t('studio.background')}
            <input type="color" value={def.bg ?? '#000000'} onChange={(e) => change((d) => ({ ...d, bg: e.target.value }))} />
          </label>
        )}
        {editable && (
          <select aria-label={t('studio.bgImage')} value={def.bgAsset ?? ''} onChange={(e) => change((d) => { const n = { ...d }; if (e.target.value) n.bgAsset = e.target.value; else delete n.bgAsset; return n; })}>
            <option value="">{t('studio.noBgImage')}</option>
            {images.map((m) => <option key={m.id} value={m.current!.sha256}>{m.name}</option>)}
          </select>
        )}
        <span className="pill neutral plain">{layout.orientation === 'portrait' ? '9:16' : '16:9'}</span>
        <span className="grow" />
        <button className="btn" onClick={() => void preview()} title={t('preview.openHint')}><Icon name="play" />{t('preview.open')}</button>
        {editable ? (
          <>
            <span className="muted small" role="status">{dirty ? t('studio.unsaved') : layout.latestRevisionNo ? t('studio.revision', { no: layout.latestRevisionNo }) : t('studio.notPublished')}</span>
            <button className="btn" disabled={busy} onClick={() => void save(false)}>{t('common.save')}</button>
            <PendingPill request={pending} />
            <button className="btn primary" disabled={busy} onClick={() => void save(true)}><Icon name="send" />{approval ? t('approval.submit.button') : t('studio.publish')}</button>
          </>
        ) : (
          <EditCopyButton layout={layout} back={params.get('back') ?? undefined} onCopied={(copy) => { setDef(null); navigate(`/studio/${copy.id}${params.get('back') ? `?back=${encodeURIComponent(back)}` : ''}`); }} />
        )}
      </div>
      <div className="d-body">
        <div className="d-tools">
          {disabledWidgets(me, def).length > 0 && (
            <div className="hint warn" role="note" style={{ marginBottom: 8 }} data-testid="studio-feature-hint">
              {t('features.studioHint', { list: disabledWidgets(me, def).map((w) => t(`widget.${w}`)).join(', ') })}
            </div>
          )}
          {editable ? (
            <>
              <div className="lbl">{t('tedit.addTitle')}</div>
              <div className="muted small tip" style={{ padding: '0 8px 4px' }}>{t('tedit.addHint')}</div>
              {(['layout', ...(Object.keys(WIDGET_TYPES) as ElementType[]).filter((k) => k !== 'layout')] as ElementType[]).filter((k) => widgetAllowed(me, k)).map((k) => (
                <button key={k} onClick={() => add(k)}><Icon name={WIDGET_ICONS[k] ?? 'plus'} />{t(`widget.${k}`)}</button>
              ))}
              <SplitButtons onSplit={split} />
              <div className="lbl tip" style={{ marginTop: 8 }}>{t('design.tip')}</div>
              <div className="muted small tip" style={{ padding: '0 8px' }}>{t('studio.tip')}</div>
            </>
          ) : <ReadonlyPanel layout={layout} back={params.get('back') ?? undefined} />}
        </div>
        <div className="d-stage" onPointerDown={(e) => { if (!(e.target as HTMLElement).closest('.d-box')) setSel(null); }}>
          <div className={`d-frame ${layout.orientation}`} ref={frame}>
            {/* Zonenvorlagen (specVersion 1) unverändert zeigen; bearbeitbar erst als eigene Kopie */}
            <LayoutPreview definition={(layout.definition as { specVersion?: number }).specVersion === 1 ? layout.definition : def} media={media} layoutId={id}
              {...(allLayouts ? { layouts: allLayouts } : {})} />
            {(layout.definition as { specVersion?: number }).specVersion === 2 && (
              <div className="d-overlay">
                {def.elements.map((e0) => {
                  const e = shown(e0);
                  return (
                    <div key={e.id} className={`d-box${sel === e.id ? ' sel' : ''}`} data-id={e.id} onPointerDown={(ev) => startDrag(ev, e0)}
                      style={{ left: `${e.x}%`, top: `${e.y}%`, width: `${e.w}%`, height: `${e.h}%` }} data-testid="studio-box">
                      <span className="tag">{t(`widget.${e.type}`)}</span>
                      <i data-h="nw" /><i data-h="ne" /><i data-h="sw" /><i data-h="se" />
                    </div>
                  );
                })}
                {guides.x !== null && <div className="d-guide" style={{ left: `${guides.x}%`, top: 0, bottom: 0, width: 1 }} />}
                {guides.y !== null && <div className="d-guide" style={{ top: `${guides.y}%`, left: 0, right: 0, height: 1 }} />}
              </div>
            )}
          </div>
        </div>
        <div className="d-props">
          {!selected ? (
            <>
              <h3 style={{ margin: 0 }}>{t('studio.slide')}</h3>
              {editable && <div className="hint">{t('studio.emptyHint')}</div>}
              {(layout.definition as { specVersion?: number }).specVersion !== 1 && <div className="muted small">{t('studio.elementCount', { count: def.elements.length })}</div>}
              {def.elements.map((e) => (
                <button key={e.id} className="btn sm" style={{ justifyContent: 'start' }} onClick={() => setSel(e.id)}><Icon name={WIDGET_ICONS[e.type] ?? 'plus'} />{t(`widget.${e.type}`)}</button>
              ))}
            </>
          ) : (
            <Props el={selected} selfId={id} media={media} disabled={!editable} uploads={editable && can(me, 'media.upload') ? uploads : undefined} onPatch={(p) => patchEl(selected.id, p)}
              onLayer={layer} onDuplicate={duplicate} onRemove={remove} />
          )}
        </div>
      </div>
      {submit && <SubmitDialog type="layout" id={id} name={name.trim() || layout.name} onClose={() => setSubmit(false)}
        onSubmitted={() => { if (params.get('back')) navigate(back); }} />}
    </div>
  );
}

type FieldKind = 'text' | 'textarea' | 'number' | 'range' | 'color' | 'check' | 'select' | 'hint' | 'secret' | 'asset' | 'assets' | 'rows' | 'coords' | 'tz'
  | 'source' | 'columns' | 'column' | 'prayer' | 'place';
type Rec = Record<string, unknown>;
/** 4. Eintrag: nur für diese Quellen (Kamera) bzw. nur wenn die Bedingung gilt (z. B. analoge Uhr). */
type FieldDef = [key: string, kind: FieldKind, opt?: unknown, when?: string[] | ((rec: Rec) => boolean)];

/** Koordinate aus der Ortssuche auf 4 Nachkommastellen (≈ 10 m, wie das Eingabefeld). */
const coord = (v: number) => Math.round(v * 10_000) / 10_000;
const analog = (r: Rec) => isAnalogFace(r.face);
const digital = (r: Rec) => !isAnalogFace(r.face);
/** Farbfeld einer analogen Uhr: ohne eigene Farbe zeigt es die Vorgabe des Zifferblatts. */
const faceDefault = (k: 'face' | 'ink' | 'accent') => (r: Rec) => (isAnalogFace(r.face) ? faceColors(r.face, r)[k] : '#ffffff');

/** Eigenschaften je Baustein (aus dem Prototyp, Beschriftungen über i18n studio.f.*). */
const FIELDS: Record<ElementType, FieldDef[]> = {
  text: [['text', 'textarea'], ['size', 'range', [2, 25, 0.5]], ['color', 'color'], ['align', 'select', ['left', 'center', 'right']], ['bold', 'check']],
  image: [['asset', 'asset', 'image'], ['fit', 'select', ['cover', 'contain']]],
  slideshow: [['assets', 'assets'], ['interval', 'number'], ['fit', 'select', ['cover', 'contain']]],
  video: [['asset', 'asset', 'video'], ['fit', 'select', ['cover', 'contain']]],
  clock: [['face', 'select', [...CLOCK_FACES]], ['mode', 'select', ['both', 'time', 'date'], digital], ['mode', 'select', ['both', 'time'], analog],
    ['seconds', 'check', null, digital], ['secondHand', 'check', null, analog], ['caption', 'text', null, analog], ['timeZone', 'tz'],
    ['faceColor', 'color', faceDefault('face'), analog], ['handColor', 'color', faceDefault('ink'), analog], ['accent', 'color', faceDefault('accent'), analog],
    ['size', 'range', [2, 30, 0.5]], ['color', 'color'], ['align', 'select', ['left', 'center', 'right'], digital]],
  weather: [['place', 'place'], ['coords', 'coords'], ['days', 'select', ['0', '1', '2', '3', '4']], ['size', 'range', [2, 25, 0.5]], ['color', 'color']],
  web: [['url', 'text'], ['zoom', 'range', [25, 200, 5]], ['reload', 'number'], ['webHint', 'hint']],
  camera: [['source', 'select', ['rtsp', 'url', 'device']], ['url', 'text', null, ['url']], ['refresh', 'number', null, ['url']], ['cameraUrlHint', 'hint', null, ['url']],
    ['name', 'text', null, ['rtsp']], ['rtspUrl', 'secret', null, ['rtsp']], ['cameraRtspHint', 'hint', null, ['rtsp']], ['fit', 'select', ['cover', 'contain']]],
  ticker: [['text', 'textarea'], ['speed', 'number'], ['size', 'range', [2, 15, 0.5]], ['color', 'color'], ['bold', 'check']],
  schedule: [['title', 'text'], ['size', 'range', [1.5, 8, 0.1]], ['color', 'color'], ['rows', 'rows']],
  prayer: [['prayer', 'prayer']],
  // Datenquellen (Feed, Kalender, Tabelle): Quelle wählen, Darstellung
  feed: [['source', 'source', 'feed'], ['mode', 'select', ['ticker', 'list', 'cards']], ['count', 'number'], ['images', 'check', null, (r) => r.mode !== 'ticker'],
    ['showDate', 'check'], ['showText', 'check', null, (r) => r.mode !== 'ticker'], ['speed', 'number', null, (r) => !r.mode || r.mode === 'ticker'],
    ['interval', 'number', null, (r) => r.mode === 'cards'], ['title', 'text', null, (r) => r.mode === 'list'], ['size', 'range', [2, 15, 0.5]], ['color', 'color'], ['accent', 'color']],
  calendar: [['source', 'source', 'calendar'], ['range', 'select', ['today', 'week']], ['view', 'select', ['list', 'day']], ['title', 'text'],
    ['showLocation', 'check'], ['maxItems', 'number', null, (r) => r.view !== 'day'], ['size', 'range', [1.5, 10, 0.1]], ['color', 'color'], ['accent', 'color']],
  table: [['source', 'source', 'table'], ['columns', 'columns'], ['today', 'column'], ['header', 'check'], ['rowsPerPage', 'number'], ['pageSeconds', 'number'],
    ['stripes', 'check'], ['title', 'text'], ['size', 'range', [1.5, 10, 0.1]], ['color', 'color'], ['accent', 'color'], ['headColor', 'color']],
  // Baustein „Vorlage": eigene Eigenschaften (EmbedProps)
  layout: [],
};

function Props({ el, selfId, media, disabled, uploads, onPatch, onLayer, onDuplicate, onRemove }: {
  el: El; selfId: string; media: MediaIndex; disabled: boolean; uploads?: StudioUploads | undefined; onPatch: (p: Partial<LayoutElement>) => void; onLayer: (d: number) => void; onDuplicate: () => void; onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [editSecret, setEditSecret] = useState(false);
  const rec = el as unknown as Rec;
  const isData = el.type === 'feed' || el.type === 'calendar' || el.type === 'table';
  const { data: sources = [] } = useDataSources({ enabled: isData });
  const { data: tableData } = useDataSourceData(el.type === 'table' && typeof rec.source === 'string' && rec.source ? rec.source : null);
  const cols = tableData?.data.kind === 'table' ? tableData.data.columns : [];
  const source = (rec.source as string | undefined) ?? 'url';
  const set = (k: string, v: unknown) => onPatch({ [k]: v } as Partial<LayoutElement>);
  const label = (k: string) => t(`studio.f.${k}`);

  const field = ([key, kind, opt, when]: FieldDef) => {
    if (when && !(typeof when === 'function' ? when(rec) : when.includes(source))) return null;
    const v = rec[key];
    switch (kind) {
      case 'prayer': return <PrayerFields key={key} rec={rec} disabled={disabled} set={set} patch={(p) => onPatch(p as Partial<LayoutElement>)} />;
      case 'place': return (
        <div key={key} className="col" style={{ gap: 4 }}>
          <PlaceSearch label={label(key)} value={(v as string) ?? ''} disabled={disabled} onText={(x) => set(key, x)}
            onPick={(p) => onPatch({ place: p.name, lat: coord(p.lat), lon: coord(p.lon) } as Partial<LayoutElement>)} />
          <span className="small muted">{t('placeSearch.hint')}</span>
        </div>
      );
      case 'text': return <label key={key} className="field">{label(key)}<input disabled={disabled} value={(v as string) ?? ''} onChange={(e) => set(key, e.target.value)} /></label>;
      case 'textarea': return <label key={key} className="field">{label(key)}<textarea disabled={disabled} value={(v as string) ?? ''} onChange={(e) => set(key, e.target.value)} /></label>;
      case 'number': return <label key={key} className="field">{label(key)}<input type="number" min={0} disabled={disabled} value={(v as number) ?? ''} onChange={(e) => set(key, Number(e.target.value))} /></label>;
      case 'range': {
        const [min, max, step] = opt as [number, number, number];
        return <label key={key} className="field">{label(key)} <span className="muted small">{String(v ?? '')}</span><input type="range" min={min} max={max} step={step} disabled={disabled} value={(v as number) ?? min} onChange={(e) => set(key, Number(e.target.value))} /></label>;
      }
      case 'color': return <label key={key} className="field">{label(key)}<input type="color" disabled={disabled} value={(v as string) || (typeof opt === 'function' ? (opt as (r: Rec) => string)(rec) : '#ffffff')} onChange={(e) => set(key, e.target.value)} /></label>;
      case 'check': return <label key={key} className="check"><input type="checkbox" disabled={disabled} checked={!!v} onChange={(e) => set(key, e.target.checked)} />{label(key)}</label>;
      case 'select': return (
        <label key={key} className="field">{label(key)}
          <select disabled={disabled} value={String(v ?? (key === 'face' ? 'digital' : ''))}
            // Neues Zifferblatt: eigene Farben zurücksetzen, damit dessen Vorgaben greifen.
            onChange={(e) => (key === 'face' ? onPatch({ face: e.target.value, faceColor: undefined, handColor: undefined, accent: undefined } as Partial<LayoutElement>)
              : set(key, key === 'days' ? Number(e.target.value) : e.target.value))}>
            {(opt as string[]).map((o) => <option key={o} value={o}>{t(`studio.o.${key}.${o}`, { defaultValue: o })}</option>)}
          </select>
        </label>
      );
      case 'tz': return (
        <label key={key} className="field">{label(key)}
          <select disabled={disabled} value={(v as string) ?? ''} onChange={(e) => set(key, e.target.value || undefined)}>
            <option value="">{t('studio.zoneLocal')}</option>
            {[...new Set([...(v ? [v as string] : []), ...CLOCK_TIME_ZONES])].map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
      );
      case 'hint': return <div key={key} className="hint small">{t(`studio.f.${key}`)}</div>;
      case 'source': {
        const list = sources.filter((s) => s.kind === opt);
        return (
          <label key={key} className="field">{t('studio.f.dataSource')}
            <select disabled={disabled} value={(v as string) ?? ''} onChange={(e) => onPatch({ source: e.target.value, ...(el.type === 'table' ? { columns: [], today: undefined } : {}) } as Partial<LayoutElement>)}>
              <option value="">{t('design.choose')}</option>
              {list.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span className="small muted">{list.length ? t('studio.f.dataSourceHint') : t('studio.f.dataSourceNone')} <Link to="/datasources">{t('datasources.manage')}</Link></span>
          </label>
        );
      }
      case 'columns': {
        const sel = (v as string[] | undefined) ?? [];
        if (!cols.length) return null;
        return (
          <div key={key} className="field">{label(key)}
            <div className="col" style={{ gap: 2 }}>
              {cols.map((c) => (
                <label key={c.key} className="check"><input type="checkbox" disabled={disabled} checked={!sel.length || sel.includes(c.key)}
                  onChange={(e) => {
                    const cur = sel.length ? sel : cols.map((x) => x.key);
                    const next = e.target.checked ? cols.map((x) => x.key).filter((k) => cur.includes(k) || k === c.key) : cur.filter((k) => k !== c.key);
                    set('columns', next.length === cols.length ? [] : next);
                  }} />{c.label}</label>
              ))}
            </div>
          </div>
        );
      }
      case 'column': return cols.length ? (
        <label key={key} className="field">{label(key)}
          <select disabled={disabled} value={(v as string) ?? ''} onChange={(e) => set(key, e.target.value || undefined)}>
            <option value="">{t('studio.o.today.all')}</option>
            {cols.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <span className="small muted">{t('studio.f.todayHint')}</span>
        </label>
      ) : null;
      case 'secret': {
        if (v && !editSecret) {
          let masked = '••••••••';
          try { const u = new URL(String(v)); masked = `${u.protocol}//${u.host}/••••••••`; } catch { /* nur maskiert */ }
          return (
            <div key={key} className="field">{label(key)}
              <div className="row"><input className="grow" value={masked} disabled /><button className="btn sm" disabled={disabled} onClick={() => setEditSecret(true)}>{t('common.change')}</button></div>
            </div>
          );
        }
        return <label key={key} className="field">{label(key)}<input disabled={disabled} defaultValue="" placeholder="rtsp://user:pass@192.168.1.20:554/…" autoComplete="off" spellCheck={false}
          onChange={(e) => set(key, e.target.value.trim())} onBlur={() => setEditSecret(false)} /></label>;
      }
      case 'asset': {
        const kind = opt as 'image' | 'video';
        const empty = !media.list.some((m) => m.kind === kind && m.current?.status === 'ready');
        return (
          <UploadArea key={key} elId={el.id} field="asset" kind={kind} uploads={uploads} canUpload={!disabled} empty={empty}>
            {empty ? <div className="field">{t(`studio.f.${kind}`)}</div>
              : <AssetSelect label={t(`studio.f.${kind}`)} kind={kind} value={v as string | undefined} media={media.list} disabled={disabled} onChange={(x) => set('asset', x)} />}
          </UploadArea>
        );
      }
      case 'assets': {
        const selList = (v as string[] | undefined) ?? [];
        const imgs = media.list.filter((m) => m.kind === 'image' && m.current?.status === 'ready');
        return (
          <UploadArea key={key} elId={el.id} field="assets" kind="image" uploads={uploads} canUpload={!disabled} empty={!imgs.length}>
            <div className="field">{label(key)}
              {imgs.length > 0 && <div className="pick">
                {imgs.map((m) => (
                  <label key={m.id} title={m.name}>
                    <input type="checkbox" disabled={disabled} checked={selList.includes(m.current!.sha256)}
                      onChange={(e) => set('assets', e.target.checked ? [...selList, m.current!.sha256] : selList.filter((x) => x !== m.current!.sha256))} />
                    <img src={mediaUrl(m)} alt={m.name} />
                  </label>
                ))}
              </div>}
            </div>
          </UploadArea>
        );
      }
      case 'coords': return (
        <div key={key} className="two">
          <label className="field">{t('studio.f.lat')}<input type="number" step="0.0001" disabled={disabled} value={(rec.lat as number | null) ?? ''} onChange={(e) => set('lat', e.target.value === '' ? null : Number(e.target.value))} /></label>
          <label className="field">{t('studio.f.lon')}<input type="number" step="0.0001" disabled={disabled} value={(rec.lon as number | null) ?? ''} onChange={(e) => set('lon', e.target.value === '' ? null : Number(e.target.value))} /></label>
        </div>
      );
      case 'rows': {
        const rows = (v as { time?: string; course?: string; trainer?: string; room?: string }[] | undefined) ?? [];
        const upd = (i: number, p: object) => set('rows', rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
        return (
          <div key={key} className="field">{label(key)}
            <div className="rows-edit">
              {rows.map((r, i) => (
                <div key={i} className="r">
                  <input aria-label={t('studio.f.time')} placeholder={t('studio.f.time')} disabled={disabled} value={r.time ?? ''} onChange={(e) => upd(i, { time: e.target.value })} />
                  <input aria-label={t('studio.f.course')} placeholder={t('studio.f.course')} disabled={disabled} value={r.course ?? ''} onChange={(e) => upd(i, { course: e.target.value })} />
                  <button className="btn sm ghost icon" disabled={disabled} aria-label={t('common.remove')} onClick={() => set('rows', rows.filter((_, j) => j !== i))}><Icon name="x" /></button>
                </div>
              ))}
              <button className="btn sm" disabled={disabled} onClick={() => set('rows', [...rows, { time: '', course: '' }])}><Icon name="plus" />{t('studio.addRow')}</button>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <>
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}><Icon name={WIDGET_ICONS[el.type] ?? 'plus'} /> {t(`widget.${el.type}`)}</h3>
        {!disabled && <>
          <button className="btn sm ghost icon" title={t('studio.front')} aria-label={t('studio.front')} onClick={() => onLayer(1)}><Icon name="front" /></button>
          <button className="btn sm ghost icon" title={t('studio.back')} aria-label={t('studio.back')} onClick={() => onLayer(-1)}><Icon name="back" /></button>
          <button className="btn sm ghost icon" title={t('playlists.duplicate')} aria-label={t('playlists.duplicate')} onClick={onDuplicate}><Icon name="copy" /></button>
          <button className="btn sm ghost icon danger" title={t('common.delete')} aria-label={t('common.delete')} onClick={onRemove}><Icon name="trash" /></button>
        </>}
      </div>
      {el.type === 'layout' && <EmbedProps el={el} selfId={selfId} media={media} disabled={disabled} onPatch={(p) => onPatch(p as Partial<LayoutElement>)} />}
      {FIELDS[el.type].map(field)}
      <div className="field">{t('studio.position')}
        <div className="four">
          {(['x', 'y', 'w', 'h'] as const).map((k) => (
            <input key={k} type="number" step={0.5} disabled={disabled} title={t(`studio.pos.${k}`)} aria-label={t(`studio.pos.${k}`)} value={round(el[k])}
              onChange={(e) => onPatch({ [k]: Number(e.target.value) })} />
          ))}
        </div>
      </div>
      <div className="two">
        <div className="field">{t('studio.boxBg')}
          <div className="row">
            <input type="checkbox" aria-label={t('studio.boxBg')} disabled={disabled} checked={!!el.bg} onChange={(e) => onPatch({ bg: e.target.checked ? '#1b1b1b' : '' })} />
            {el.bg ? <input type="color" aria-label={t('studio.f.color')} disabled={disabled} value={el.bg} onChange={(e) => onPatch({ bg: e.target.value })} /> : null}
          </div>
        </div>
        <label className="field">{t('studio.radius')}<input type="range" min={0} max={5} step={0.5} disabled={disabled} value={el.radius ?? 0} onChange={(e) => onPatch({ radius: Number(e.target.value) })} /></label>
      </div>
    </>
  );
}
